// RevenueCat wrapper — single entry point for all native (iOS/Android) purchases.
// Web continues to use Stripe Checkout via the existing /api/payments/checkout flow.
// All plugin calls are dynamically imported so this file is safe to use on web.

import { isNativeApp, getPlatform } from "./platform";

// Read at build time from frontend/.env (or .env.production).
// These are PUBLIC keys — safe to bundle into the mobile app.
const IOS_KEY = process.env.REACT_APP_REVENUECAT_IOS_KEY || "";
const ANDROID_KEY = process.env.REACT_APP_REVENUECAT_ANDROID_KEY || "";

// Map our internal package IDs <-> the store product IDs RevenueCat returns.
// Configure these IDs identically in App Store Connect + Google Play Console.
export const PACKAGE_PRODUCT_IDS = {
  "5_readings": "5_readings",
  "20_readings": "20_readings",
  "50_readings": "50_readings",
  "unlimited_monthly": "unlimited_monthly",
  "live_session": "live_session",
};

let _configured = false;
let _configuring = null;
// Resolves once the SDK is configured for the signed-in user. Offerings and
// purchases wait on it, so a screen that opens first (for example the Plans
// page on a cold start) never calls the SDK before it is set up, and purchases
// are always tied to the user's id rather than an anonymous one.
let _markReady;
const _ready = new Promise((resolve) => { _markReady = resolve; });

async function whenReady(timeoutMs = 15000) {
  if (_configured) return true;
  return Promise.race([_ready.then(() => true), new Promise((r) => setTimeout(() => r(false), timeoutMs))]);
}

export function isRevenueCatEnabled() {
  if (!isNativeApp()) return false;
  const platform = getPlatform();
  if (platform === "ios" && !IOS_KEY) return false;
  if (platform === "android" && !ANDROID_KEY) return false;
  return true;
}

export async function configureRevenueCat(userId) {
  if (!isRevenueCatEnabled()) return;
  if (_configuring && !_configured) await _configuring;
  if (_configured) {
    // If already configured, just identify the user.
    if (userId) {
      try {
        const { Purchases } = await import("@revenuecat/purchases-capacitor");
        await Purchases.logIn({ appUserID: userId });
      } catch (e) { /* ignore */ }
    }
    return;
  }
  _configuring = (async () => {
    try {
      const { Purchases, LOG_LEVEL } = await import("@revenuecat/purchases-capacitor");
      const platform = getPlatform();
      const apiKey = platform === "ios" ? IOS_KEY : ANDROID_KEY;
      await Purchases.setLogLevel({ level: LOG_LEVEL.WARN });
      await Purchases.configure({ apiKey, appUserID: userId || undefined });
      _configured = true;
      _markReady();
    } catch (e) {
      console.error("RevenueCat configure failed:", e);
    } finally {
      _configuring = null;
    }
  })();
  await _configuring;
}

export async function getOfferings() {
  if (!isRevenueCatEnabled()) return null;
  if (!(await whenReady())) return null;
  try {
    const { Purchases } = await import("@revenuecat/purchases-capacitor");
    const offerings = await Purchases.getOfferings();
    return offerings?.current || null;
  } catch (e) {
    console.error("getOfferings failed:", e);
    return null;
  }
}

export async function purchasePackage(pkg) {
  if (!isRevenueCatEnabled()) throw new Error("RevenueCat not enabled");
  if (!(await whenReady())) throw new Error("RevenueCat not ready");
  const { Purchases } = await import("@revenuecat/purchases-capacitor");
  const result = await Purchases.purchasePackage({ aPackage: pkg });
  return result;
}

export async function restorePurchases() {
  if (!isRevenueCatEnabled()) return null;
  if (!(await whenReady())) return null;
  try {
    const { Purchases } = await import("@revenuecat/purchases-capacitor");
    const info = await Purchases.restorePurchases();
    return info;
  } catch (e) {
    console.error("restorePurchases failed:", e);
    return null;
  }
}
